/**
 * Observability — the ONE entry point for a frontend event (#382).
 *
 * The backend has `services/observability.ts`, whose `record()` also ROUTES:
 * a call that names a `subject` writes an `audit_log` row as well. The
 * frontend has one sink, so there is nothing to route here.
 *
 * It is still a service, for the reason the backend one is worth having:
 * ONE place to change what every event carries. `ambientProps()` below is
 * the proof — the UI route and the language now ride on all 14 events, and
 * adding the next one is a single edit rather than 15. A sampling rule, a
 * second sink or a redaction pass would land in the same place.
 *
 * ── Layer ───────────────────────────────────────────────────────────────
 *
 *   observability.ts   the DOOR — components call record()
 *        └── telemetry.ts   the TRANSPORT — App Insights, imports nothing
 *
 * `logBackgroundError()` lives HERE and not in the transport, because it
 * decides WHICH event to emit: a network failure on a background poll
 * becomes an event, everything else an exception. That is a application
 * decision, and routing it through `record()` is what keeps the door
 * single. The backend draws the same line.
 *
 * ── The catalogue ───────────────────────────────────────────────────────
 *
 * `EVENTS` is the closed set. TypeScript refuses a name outside it, so a
 * typo is a build error and not a silent new event. Until this file existed
 * `trackEvent(name: string)` accepted anything, and nothing noticed a name
 * that had stopped firing.
 *
 * These events reach the SAME Log Analytics workspace as the backend ones.
 * `AppRoleName` tells them apart — `lmfapp-frontend-prod` against
 * `lmfapp-backend-prod` — so a query must say which side it means, and a
 * name may not be used by both. `frontend-event-parity.spec.ts` pins that.
 *
 * ── Public by construction ──────────────────────────────────────────────
 *
 * All of `frontend/src` is PUBLIC: production serves `.js.map` with
 * `sourcesContent`. A name and a property shape here are public too. Never
 * put a credential or an internal hostname in a property.
 */
import axios from 'axios';
import { normalisePath } from './lib/uiPath';
import { local } from './lib/safeStorage';
import { isSignOutRejection } from './lib/signOuts';
import { trackEvent, trackException } from './telemetry';

export const EVENTS = {
  login:                       'A user signed in: the browser had no user, or had another user { roles }',
  language_changed:            'The interface language changed { lang }',
  registration_submitted:      'The registration form was sent { has_phone }',
  team_join_clicked:           'A person asked to join a team { role, team_id }',
  notification_clicked:        'A notification in the bell was opened { type }',
  push_optin_prompted:         'The push opt-in was answered { action }',
  push_prompt_snoozed:         'The push banner was dismissed for now {}',
  ui_error_shown:              'An error reached the user as a toast or inline { status, code, correlationId, method, path, surface }',
  http_retry_succeeded:        'A request succeeded after a retry { url, attempts, reason }',
  background_poll_unreachable: 'A background poll failed its retry budget { context, url, reason }',
  socket_disconnect:           'The socket dropped { reason }',
  socket_reconnect:            'The socket came back { attempt }',
  app_resume:                  'A resume signal ran; each mounted live view reloaded its data (#499) { reason, listeners }',
  socket_auth_refreshed:       'An anonymous socket of a signed-in tab refreshed the token and connected again (#501) { ok }',
} as const;

export type EventName = keyof typeof EVENTS;

export interface RecordInput {
  /** Must exist in EVENTS. TypeScript refuses anything else. */
  event: EventName;
  /** Extra attributes. A value here overrides an ambient one of the same
   *  name, which is why the ambient keys carry a `ui_` prefix. */
  props?: Record<string, string>;
}

/**
 * What EVERY frontend event carries. The single place to add the next one.
 *
 * `ui_path` is the route the event fired on, which no call site knew before
 * and which turns `ui_error_shown` from "an error happened" into "an error
 * happened on this screen". `ui_lang` separates the Lithuanian and English
 * audiences without a join.
 *
 * The language is read from the site storage (`local` of
 * `lib/safeStorage.ts`) and NOT from `useI18n`, on purpose: `i18n/index.ts`
 * emits `language_changed` through this file, so importing the store here
 * would make a cycle. `safeStorage.ts` has no imports, so it adds no cycle.
 * The storage is the store's own source of truth — it initialises from that
 * key and writes it on every change — so this reads the same value one step
 * earlier.
 */
function ambientProps(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    out.ui_path = normalisePath(window.location.pathname);
  } catch { /* no window (a test importing this module) must not break */ }
  // Private mode or blocked site data gives null, and must not break an event.
  const lang = local.get('lang');
  if (lang) out.ui_lang = lang;
  return out;
}

/**
 * Record one event.
 *
 * Never throws: an observability failure must not change what the user
 * sees. `trackEvent()` is a no-op when App Insights is not configured.
 */
export function record(input: RecordInput): void {
  trackEvent(input.event, { ...ambientProps(), ...(input.props ?? {}) });
}

/**
 * What the USER saw of a refused request (2026-09-19; #504 added `surface`).
 * App Insights holds the backend's side of the request (with its
 * `error_code` since #504); this event records the message on screen, with
 * the backend's correlationId when the answer carried one, so a support
 * question ("it said error") joins to one log line. `surface` says where
 * the message showed: `toast` (`useToast().showError`) or `inline` (under a
 * field or in a row, through `inlineError()` in hooks/useApiCall.ts).
 * A failure with no answer (the network) is not a refusal: this function
 * records nothing for it.
 *
 * #666 — A sign-out ended the request: the user sees no error, so the
 * function records nothing. Every caller gets this rule here.
 */
export function recordShownError(err: unknown, surface: 'toast' | 'inline'): void {
  if (isSignOutRejection(err)) return;
  if (!(axios.isAxiosError(err) && err.response)) return;
  const data = err.response.data as { code?: string; correlationId?: string } | undefined;
  record({
    event: 'ui_error_shown',
    props: {
      status: String(err.response.status),
      code: String(data?.code ?? ''),
      correlationId: String(data?.correlationId ?? ''),
      method: String(err.config?.method ?? '').toUpperCase(),
      path: String(err.config?.url ?? '').split('?')[0],
      surface,
    },
  });
}

/** Endpoints called by background pollers (not user actions). When a request
 *  to one of these fails after the axios retry budget is exhausted, the
 *  rejection is recorded as `background_poll_unreachable` instead of an
 *  exception, so the App Insights Exceptions blade stays free of in-arena
 *  WiFi noise — the diagnostic detail is still on the event for triage.
 *  A foreground read (the user clicked something) still surfaces as an
 *  exception.
 *  The paths are literals: `api/client.ts` imports this file, so it cannot
 *  give the paths. `api-boundary-census-664.spec.ts` checks that each path is
 *  the path of a GET wrapper in `api/client.ts`. */
const BACKGROUND_POLL_PATHS = [
  '/api/version',                       // getVersion(): the VersionCheck poll of App.tsx
  '/api/notifications/unread-count',    // getUnreadCount(): the poll of NotificationBell.tsx, every 5 min
];

/** #499 — the last run of the resume signal (`lib/resume.ts`). A load in
 *  the next 15 s can run while a phone still waits for its network, so a
 *  network-level failure then is an event, not an exception. */
let lastResumeAt = -Infinity;
const RESUME_NETWORK_WINDOW_MS = 15_000;
export function markResume(): void { lastResumeAt = performance.now(); }
function inResumeWindow(): boolean {
  return performance.now() - lastResumeAt < RESUME_NETWORK_WINDOW_MS;
}

function isBackgroundPoll(url: string | undefined): boolean {
  if (!url) return false;
  return BACKGROUND_POLL_PATHS.some(p => url.includes(p));
}

/**
 * Standard handler for background/fire-and-forget fetches that would
 * otherwise be swallowed by `.catch(() => {})`. The request still fails
 * silently for the user (UX unchanged), but App Insights gets an exception
 * tagged with the call site, so a degraded read is visible.
 *
 * Usage:
 *   getTeams().then(r => setTeams(r.data)).catch(logBackgroundError('Athletes:teams'))
 */
export function logBackgroundError(context: string) {
  return (err: unknown) => {
    // #666 — A sign-out ended the request: no exception, no event.
    if (isSignOutRejection(err)) return;
    const error = err instanceof Error ? err : new Error(String(err));
    const isAxios = axios.isAxiosError(err);
    const status = isAxios ? err.response?.status : undefined;
    // 401 is handled by the axios interceptor (refresh/redirect). No point
    // reporting it again as a background exception.
    if (status === 401) return;
    const url = isAxios && typeof err.config?.url === 'string' ? err.config.url : undefined;

    // Network-level failure (no response received OR our 10s timeout fired)
    // on a known background-poll path: this is environmental, not a bug.
    // The next poll tick will likely succeed; record it as an event.
    const isNetworkError = isAxios && (err.message === 'Network Error' || err.code === 'ECONNABORTED');
    const afterResume = inResumeWindow();
    if (isNetworkError && (isBackgroundPoll(url) || afterResume)) {
      record({
        event: 'background_poll_unreachable',
        props: {
          context,
          ...(url ? { url } : {}),
          reason: err.message === 'Network Error' ? 'network' : 'timeout',
          ...(afterResume ? { after_resume: 'true' } : {}),
        },
      });
      return;
    }

    trackException(error, {
      context,
      ...(status != null ? { status: String(status) } : {}),
      ...(url ? { url } : {}),
    });
  };
}
